<template>
  <div class="site-config-admin">
    <div class="page-head">
      <div>
        <h2>网站配置管理</h2>
        <p>管理前台导航栏、Logo、首页三屏标题与背景图、页脚信息。当前启用导航：{{ visibleNavCount }} 个。</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" :loading="saving" @click="save">保存配置</el-button>
        <el-button @click="load">重新读取</el-button>
        <el-button @click="exportNav">导出导航</el-button>
        <el-button @click="exportConfig">导出全部配置</el-button>
      </div>
    </div>

    <el-alert
      title="本页面保存到项目本地 data/site-config.json；前台会读取 /site-config.json 并实时使用这些配置。"
      type="info"
      show-icon
      :closable="false"
      class="tip"
    />

    <el-tabs v-model="activeTab" type="border-card">
      <el-tab-pane label="基础信息 / Logo" name="base">
        <el-form label-width="120px">
          <el-form-item label="网站名称">
            <el-input v-model="config.siteName" placeholder="请输入网站名称" />
          </el-form-item>
          <el-form-item label="Logo 图片">
            <div class="image-line">
              <img class="logo-preview" :src="config.logoUrl || defaultLogo" alt="logo" />
              <div>
                <el-input v-model="config.logoUrl" placeholder="可粘贴图片地址，或上传图片" />
                <input class="file-input" type="file" accept="image/*" @change="uploadLogo" />
                <p class="muted">留空时使用项目默认 Logo；上传后自动生成 /uploads/... 地址。</p>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="导航栏" name="nav">
        <div class="section-toolbar">
          <span>导航总数：{{ config.navItems.length }}，启用：{{ visibleNavCount }}</span>
          <el-button size="small" type="primary" @click="addNav">新增导航</el-button>
        </div>
        <el-table :data="config.navItems" border style="width: 100%">
          <el-table-column label="显示" width="80" align="center">
            <template slot-scope="scope">
              <el-switch v-model="scope.row.visible" />
            </template>
          </el-table-column>
          <el-table-column label="名称">
            <template slot-scope="scope">
              <el-input v-model="scope.row.name" placeholder="导航名称" />
            </template>
          </el-table-column>
          <el-table-column label="路由地址">
            <template slot-scope="scope">
              <el-input v-model="scope.row.path" placeholder="例如 /news" />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="230" align="center">
            <template slot-scope="scope">
              <el-button size="mini" @click="moveNav(scope.$index, -1)" :disabled="scope.$index === 0">上移</el-button>
              <el-button size="mini" @click="moveNav(scope.$index, 1)" :disabled="scope.$index === config.navItems.length - 1">下移</el-button>
              <el-button size="mini" type="danger" @click="removeNav(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="首页三屏" name="home">
        <el-row :gutter="20">
          <el-col :span="8" v-for="(slide, index) in config.homeSlides" :key="slide.key || index">
            <el-card class="slide-card">
              <div slot="header">第 {{ index + 1 }} 屏</div>
              <el-form label-position="top">
                <el-form-item label="主标题">
                  <el-input v-model="slide.title" />
                </el-form-item>
                <el-form-item label="副标题">
                  <el-input v-model="slide.subtitle" />
                </el-form-item>
                <el-form-item label="标语/描述">
                  <el-input v-model="slide.description" placeholder="第一屏标语；其他屏可留空" />
                </el-form-item>
                <el-form-item label="背景图片">
                  <div class="bg-preview" :style="backgroundPreview(slide.backgroundUrl, index)"></div>
                  <el-input v-model="slide.backgroundUrl" placeholder="可粘贴图片地址，或上传图片" />
                  <input class="file-input" type="file" accept="image/*" @change="uploadSlideImage(index, $event)" />
                  <el-button size="mini" @click="clearSlideImage(index)">使用默认背景</el-button>
                </el-form-item>
              </el-form>
            </el-card>
          </el-col>
        </el-row>
      </el-tab-pane>

      <el-tab-pane label="页脚" name="footer">
        <div class="section-toolbar">
          <span>页脚分组：{{ config.footerGroups.length }} 个</span>
          <el-button size="small" type="primary" @click="addFooterGroup">新增分组</el-button>
        </div>
        <el-card v-for="(group, groupIndex) in config.footerGroups" :key="groupIndex" class="footer-card">
          <div slot="header" class="footer-card-head">
            <el-input v-model="group.title" placeholder="分组标题" />
            <el-button type="danger" size="mini" @click="removeFooterGroup(groupIndex)">删除分组</el-button>
          </div>
          <div v-for="(item, itemIndex) in group.items" :key="itemIndex" class="footer-item-row">
            <el-input v-model="group.items[itemIndex]" placeholder="页脚文本" />
            <el-button size="mini" type="danger" @click="removeFooterItem(groupIndex, itemIndex)">删除</el-button>
          </div>
          <el-button size="mini" @click="addFooterItem(groupIndex)">新增文本</el-button>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import defaultLogo from "../../assets/img/shangya-logo.png";
import defaultHomeTop from "../../assets/img/home_top.jpg";
import defaultHomeDo from "../../assets/img/home_do.jpg";
import defaultHomeAnli from "../../assets/img/home_anli.jpg";
import {
  getDefaultSiteConfig,
  loadEditableSiteConfig,
  saveSiteConfig,
  uploadSiteImage
} from "../../services/siteConfig";

export default {
  name: "SiteConfigAdmin",
  data() {
    return {
      activeTab: "base",
      saving: false,
      defaultLogo,
      defaultBackgrounds: [defaultHomeTop, defaultHomeDo, defaultHomeAnli],
      config: getDefaultSiteConfig()
    };
  },
  computed: {
    visibleNavCount() {
      return this.config.navItems.filter(item => item.visible !== false).length;
    }
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      loadEditableSiteConfig()
        .then(config => {
          this.config = config;
        })
        .catch(error => {
          this.$message.error(`读取配置失败：${error.message}`);
        });
    },
    save() {
      this.saving = true;
      saveSiteConfig(this.config)
        .then(config => {
          this.config = config;
          this.$message.success("网站配置已保存");
        })
        .catch(error => {
          this.$message.error(`保存失败：${error.message}`);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    addNav() {
      this.config.navItems.push({ name: "新导航", path: "/", visible: true });
    },
    removeNav(index) {
      this.config.navItems.splice(index, 1);
    },
    moveNav(index, offset) {
      const target = index + offset;
      if (target < 0 || target >= this.config.navItems.length) return;
      const item = this.config.navItems.splice(index, 1)[0];
      this.config.navItems.splice(target, 0, item);
    },
    uploadLogo(event) {
      this.uploadFile(event, url => {
        this.config.logoUrl = url;
      });
    },
    uploadSlideImage(index, event) {
      this.uploadFile(event, url => {
        const slide = Object.assign({}, this.config.homeSlides[index], {
          backgroundUrl: url
        });
        this.$set(this.config.homeSlides, index, slide);
      });
    },
    uploadFile(event, done) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      uploadSiteImage(file)
        .then(result => {
          done(result.url);
          this.$message.success("图片上传成功");
        })
        .catch(error => {
          this.$message.error(`上传失败：${error.message}`);
        });
    },
    clearSlideImage(index) {
      const slide = Object.assign({}, this.config.homeSlides[index], {
        backgroundUrl: ""
      });
      this.$set(this.config.homeSlides, index, slide);
    },
    backgroundPreview(url, index) {
      return {
        backgroundImage: `url(${url || this.defaultBackgrounds[index]})`
      };
    },
    addFooterGroup() {
      this.config.footerGroups.push({ title: "新分组", items: ["新文本"] });
    },
    removeFooterGroup(index) {
      this.config.footerGroups.splice(index, 1);
    },
    addFooterItem(groupIndex) {
      this.config.footerGroups[groupIndex].items.push("新文本");
    },
    removeFooterItem(groupIndex, itemIndex) {
      this.config.footerGroups[groupIndex].items.splice(itemIndex, 1);
    },
    exportNav() {
      const navItems = this.config.navItems.map(item => ({
        name: item.name,
        path: item.path,
        visible: item.visible !== false
      }));
      this.downloadJson("navigation-config.json", {
        count: navItems.length,
        visibleCount: navItems.filter(item => item.visible).length,
        names: navItems.map(item => item.name),
        items: navItems
      });
    },
    exportConfig() {
      this.downloadJson("site-config.json", this.config);
    },
    downloadJson(fileName, data) {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8"
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }
  }
};
</script>

<style lang="scss" scoped>
.site-config-admin {
  text-align: left;
}

.page-head,
.section-toolbar,
.footer-card-head,
.image-line,
.footer-item-row {
  display: flex;
  align-items: center;
}

.page-head {
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    margin: 0 0 8px;
  }

  p {
    margin: 0;
    color: #666;
  }
}

.head-actions > * {
  margin-left: 8px;
}

.tip {
  margin-bottom: 16px;
}

.section-toolbar {
  justify-content: space-between;
  margin-bottom: 12px;
}

.image-line {
  gap: 16px;
}

.logo-preview {
  width: 240px;
  max-height: 80px;
  padding: 12px;
  border: 1px solid #ebeef5;
  object-fit: contain;
  background: #fff;
}

.file-input {
  display: block;
  margin: 10px 0;
}

.muted {
  margin: 0;
  color: #909399;
  font-size: 13px;
}

.slide-card {
  margin-bottom: 20px;
}

.bg-preview {
  height: 130px;
  margin-bottom: 10px;
  border: 1px solid #ebeef5;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.footer-card {
  margin-bottom: 16px;
}

.footer-card-head {
  justify-content: space-between;

  .el-input {
    max-width: 360px;
  }
}

.footer-item-row {
  gap: 10px;
  margin-bottom: 10px;
}
</style>
